import { FC, useState, useCallback, useEffect } from 'react';
import {
  MessageCircle,
  Phone,
  Users,
  Calendar,
  Clock,
  X,
  CheckCircle,
  RotateCcw,
  AlertTriangle,
  MapPin,
  Edit3,
  Save,
  XCircle,
  FilePlus
} from 'lucide-react';
import { getCustomerPhoneNumbers, directusOperations } from '../../lib/directus';
import { determineInteractionStatus } from '../../lib/interactionStatusUtils';
import { isWithinAppendWindow, formatAppendedContent, getRemainingAppendDays } from '../../lib/interactionAppendUtils';
import { CustomerDetailsModal } from '../ui/CustomerDetailsModal';
import { Badge } from '../ui/badge';
import { CustomerSelectionModal } from '../ui/CustomerSelectionModal';
import { NotesModal, ProgressStatus } from '../ui/NotesModal';
import { NotesDetailModal } from '../ui/NotesDetailModal';
import { RichTextEditor } from '../ui/RichTextEditor';
import { AppendDetailsModal } from './AppendDetailsModal';
import { Customer } from '../../lib/types';
import { getWIBTimeForDirectus, getWIBTimeISO } from '../../lib/timeUtils';
import { safeHapticImpact, safeHapticNotification } from '../../lib/telegramUtils';
import { useAppDispatch } from '../../hooks/redux';
import { selectCustomer } from '../../store/slices/customersSlice';
import { setActiveVisit } from '../../store/slices/visitsSlice';
import { navigateTo } from '../../store/slices/navigationSlice';
import { useLanguage } from '../../contexts/LanguageContext';
import {
  mapInteractionToVisit,
  CustomerInteraction as MappedCustomerInteraction
} from '../../lib/interactionToVisitMapper';
import Swal from 'sweetalert2';

interface PhoneNumber {
  number: string;
  source: 'phone_customer' | 'customer_tambak';
  type: 'whatsapp' | 'phone';
  displayLabel?: string;
}

interface CustomerInteraction {
  id: number;
  No_pelanggan: string;
  customer_name: string;
  customer_tambak_id?: number;
  date_created: string;
  scheduled_date?: string;
  completed_date?: string;
  subject: string;
  keterangan?: string;
  interaction_type: 'WhatsApp' | 'Phone Call' | 'Visit/Meeting';
  status: 'completed' | 'scheduled' | 'pending' | 'cancelled';
  priority: 'low' | 'medium' | 'high';
  PIC: string;
  visit_id?: number;
  duration_minutes?: number;
  follow_up_required: boolean;
  follow_up_date?: string;
}

interface InteractionDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  interaction: CustomerInteraction | null;
  onUpdate: (interaction: CustomerInteraction) => void;
  onComplete: (interaction: CustomerInteraction) => void;
  onReschedule: (interaction: CustomerInteraction) => void;
  onCreateFollowUp: (interaction: CustomerInteraction) => void;
}

export const InteractionDetailModal: FC<InteractionDetailModalProps> = ({
  isOpen,
  onClose,
  interaction,
  onUpdate,
  onComplete,
  onReschedule: _onReschedule,
  onCreateFollowUp: _onCreateFollowUp
}) => {
  const dispatch = useAppDispatch();
  const { t, language } = useLanguage();
  const [completionNotes, setCompletionNotes] = useState('');
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [showRescheduleDialog, setShowRescheduleDialog] = useState(false);
  const [rescheduleDate, setRescheduleDate] = useState('');
  const [rescheduleType, setRescheduleType] = useState<'WhatsApp' | 'Phone Call' | 'Visit/Meeting'>('WhatsApp');
  // Removed rescheduleTime - only date needed for reschedule
  
  // Phone number selection state
  const [showPhoneModal, setShowPhoneModal] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [phoneLoading, setPhoneLoading] = useState(false);
  const [phoneError, setPhoneError] = useState<string | null>(null);

  // Language toggle state
  useState(false);

  const getStatusVariant = (status: string) => {
    switch (status) {
      case 'completed': return 'default';
      case 'overdue': return 'destructive';
      case 'pending': return 'secondary';
      case 'scheduled': return 'outline';
      default: return 'secondary';
    }
  };
  const [, setSelectedAction] = useState<'call' | 'whatsapp' | null>(null);
  
  // Notes modal state for keterangan workflow
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<{
    type: 'call' | 'whatsapp';
    phoneNumber: PhoneNumber;
  } | null>(null);
  
  // Customer modal state for Visit button
  const [showCustomerDetails, setShowCustomerDetails] = useState(false);
  const [showCustomerSelection, setShowCustomerSelection] = useState(false);
  const [customerAction, setCustomerAction] = useState<'call' | 'whatsapp' | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);

  // Notes editing state
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [editedNotes, setEditedNotes] = useState('');
  const [isSavingNotes, setIsSavingNotes] = useState(false);

  // Append details state (for completed interactions within 7-day window)
  const [showAppendModal, setShowAppendModal] = useState(false);

  // Notes detail modal state (for showing full notes)
  const [showNotesDetailModal, setShowNotesDetailModal] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
      }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  // Reset edit mode when modal closes
  useEffect(() => {
    if (!isOpen) {
      setIsEditingNotes(false);
      setEditedNotes('');
    }
  }, [isOpen]);

  const fetchPhoneNumbers = useCallback(async () => {
    if (!interaction?.No_pelanggan) return;
    
    setPhoneLoading(true);
    setPhoneError(null);
    
    try {
      const numbers = await getCustomerPhoneNumbers(interaction.No_pelanggan);
      setPhoneNumbers(numbers);
    } catch (err) {
      console.error('Failed to fetch phone numbers:', err);
      setPhoneError('Failed to load phone numbers');
    } finally {
      setPhoneLoading(false);
    }
  }, [interaction?.No_pelanggan]);

  useEffect(() => {
    if (interaction) {
      // Reset edit mode when opening new interaction
      setIsEditingNotes(false);
      // Preload phone numbers for quick actions
      fetchPhoneNumbers();
      // Initialize notes editing state
      setEditedNotes(interaction.keterangan || '');
    }
  }, [interaction, fetchPhoneNumbers]);

  // Load phone numbers when notes modal opens (for direct contact feature)
  useEffect(() => {
    if (showNotesModal && phoneNumbers.length === 0 && !phoneLoading && !phoneError) {
      console.log('📞 Loading phone numbers for NotesModal direct contact feature');
      fetchPhoneNumbers();
    }
  }, [showNotesModal, phoneNumbers.length, phoneLoading, phoneError, fetchPhoneNumbers]);


  const handleComplete = useCallback(() => {
    if (interaction) {
      const completedInteraction = {
        ...interaction,
        status: 'completed' as const,
        completed_date: getWIBTimeForDirectus(),
        keterangan: completionNotes || interaction.keterangan
      };
      onComplete(completedInteraction);
      setShowCompleteDialog(false);
      
      // Haptic feedback
      if (safeHapticImpact) {
        safeHapticImpact('medium');
      }
    }
  }, [interaction, completionNotes, onComplete]);

  const handleReschedule = useCallback(async () => {
    if (interaction && rescheduleDate && rescheduleType) {
      try {
        // NEW APPROACH: Create a new interaction and hide the old one
        console.log('🔄 Rescheduling: Creating new interaction and hiding old one', interaction.id);

        // Step 1: Create new interaction with copied data (except date_created and done)
        const newInteraction = await directusOperations.createCustomerInteraction({
          No_pelanggan: interaction.No_pelanggan,
          keterangan: (interaction.keterangan || '') + '\n\n[Rescheduled from ' + interaction.date_created + ' to ' + rescheduleDate + ']',
          interaction_type: rescheduleType,
          customer_tambak_id: interaction.customer_tambak_id,
          visit_id: interaction.visit_id,
          priority: interaction.priority,
          date_created: rescheduleDate, // NEW: Use rescheduled date
          done: null, // NEW: Set to pending (not done)
          Progress: (interaction as any).Progress || null
        });

        console.log('✅ Created new interaction:', newInteraction.id);

        // Step 2: Hide the old interaction by setting Hidden = true
        await directusOperations.updateCustomerInteraction(interaction.id, {
          Hidden: true,
          keterangan: (interaction.keterangan || '') + '\n\n[Rescheduled to ' + rescheduleDate + ' - See interaction #' + newInteraction.id + ']'
        });

        console.log('✅ Hidden old interaction:', interaction.id);

        // Show success message
        await Swal.fire({
          icon: 'success',
          title: 'Rescheduled Successfully',
          text: `Created new interaction #${newInteraction.id} for ${rescheduleDate}`,
          confirmButtonText: 'OK',
          confirmButtonColor: 'rgb(var(--status-success))'
        });

        setShowRescheduleDialog(false);
        onClose(); // Close modal after reschedule

        // Haptic feedback for success
        safeHapticNotification('success');

        // Reload interactions list to reflect changes
        window.location.reload();
      } catch (error) {
        console.error('Failed to reschedule interaction:', error);

        // Show error message
        await Swal.fire({
          icon: 'error',
          title: 'Reschedule Failed',
          text: 'Could not create new interaction. Please try again.',
          confirmButtonText: 'OK',
          confirmButtonColor: 'hsl(var(--destructive))'
        });

        // Haptic feedback for error
        safeHapticNotification('error');
      }
    }
  }, [interaction, rescheduleDate, rescheduleType, onClose]);

  const getInteractionTypeIcon = (type: string) => {
    switch (type) {
      case 'WhatsApp':
        return <MessageCircle className="w-5 h-5" />;
      case 'Phone Call':
        return <Phone className="w-5 h-5" />;
      case 'Visit/Meeting':
        return <Users className="w-5 h-5" />;
      default:
        return <MessageCircle className="w-5 h-5" />;
    }
  };


  const formatDateTime = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });
  };


  const handleContactAction = useCallback(async (action: 'whatsapp' | 'call' | 'visit') => {
    if (action === 'visit') {
      // NEW: Direct navigation to ActiveVisit with pre-filled data
      if (!interaction) return;
      
      // IMPROVEMENT: Update interaction_type before starting visit
      const updatedInteractionType = 'Visit/Meeting';
      
      // Update interaction in database with correct type
      try {
        await directusOperations.updateCustomerInteraction(interaction.id, {
          interaction_type: updatedInteractionType
        });
        console.log('✅ Updated interaction_type to:', updatedInteractionType);
      } catch (error) {
        console.warn('⚠️ Failed to update interaction_type:', error);
      }

      try {
        // Get user's current GPS coordinates
        let coordinates = { lat: 0, lng: 0 };
        
        try {
          const position = await new Promise<GeolocationPosition>((resolve, reject) => {
            if (!navigator.geolocation) {
              reject(new Error('Geolocation not supported'));
              return;
            }
            
            navigator.geolocation.getCurrentPosition(
              resolve,
              reject,
              { 
                enableHighAccuracy: true,
                timeout: 10000,
                maximumAge: 60000 
              }
            );
          });
          
          coordinates = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
        } catch (error) {
          console.warn('Failed to get GPS coordinates:', error);
          // Continue with default coordinates (0, 0) - user can update location later
        }

        // Map interaction to visit data
        const visitData = mapInteractionToVisit(
          interaction as MappedCustomerInteraction,
          coordinates,
          '' // Employee ID will be filled by the app
        );

        // Show simple confirmation message
        const result = await Swal.fire({
          icon: 'info',
          title: 'Starting Visit',
          text: 'Starting visit from customer interaction...',
          confirmButtonText: 'Start Visit',
          confirmButtonColor: '#0099ff',
          cancelButtonText: 'Cancel',
          showCancelButton: true,
          allowOutsideClick: false
        });

        if (!result.isConfirmed) return;

        // Set active visit in Redux store with pre-filled data
        dispatch(setActiveVisit(visitData));
        
        // Navigate directly to ActiveVisit screen
        dispatch(navigateTo('visitActive'));
        
        // Close the interaction modal
        onClose();
        
        // Haptic feedback
        if (safeHapticNotification) {
          (window as any).Telegram.WebApp.HapticFeedback.notificationOccurred('success');
        }
        
        console.log('✅ Visit started successfully from interaction:', interaction.id);
        
      } catch (error) {
        console.error('❌ Failed to start visit from interaction:', error);
        
        await Swal.fire({
          icon: 'error',
          title: 'Failed to Start Visit',
          text: 'An error occurred while preparing the visit. Please try again.',
          confirmButtonText: 'OK',
          confirmButtonColor: 'hsl(var(--status-error))'
        });
        
        // Haptic feedback for error
        if (safeHapticNotification) {
          (window as any).Telegram.WebApp.HapticFeedback.notificationOccurred('error');
        }
      }
      return;
    }

    // For call/whatsapp, open phone selection modal
    setSelectedAction(action);
    setShowPhoneModal(true);
    fetchPhoneNumbers();
  }, [interaction, fetchPhoneNumbers, dispatch, onClose]);

  // Customer modal handlers
  const handleCustomerDetailsCall = useCallback((customer: Customer) => {
    setShowCustomerDetails(false);
    setSelectedCustomer(customer);
    setCustomerAction('call');
    setShowCustomerSelection(true);
  }, []);

  const handleCustomerDetailsWhatsApp = useCallback((customer: Customer) => {
    setShowCustomerDetails(false);
    setSelectedCustomer(customer);
    setCustomerAction('whatsapp');
    setShowCustomerSelection(true);
  }, []);

  const handleCustomerActionComplete = useCallback((action: 'call' | 'whatsapp', number: string, customer: Customer) => {
    console.log(`${action} action performed for ${customer.name} at ${number}`);
    
    // Close all customer modals
    setShowCustomerSelection(false);
    setShowCustomerDetails(false);
    setSelectedCustomer(null);
    setCustomerAction(null);
    
    // Haptic feedback
    if (safeHapticNotification) {
      (window as any).Telegram.WebApp.HapticFeedback.impactOccurred('light');
    }
  }, []);

  const handleStartVisit = useCallback(async (customer: Customer) => {
    try {
      console.log('Starting visit for customer:', customer);
      
      if (!customer) {
        console.error('Customer is null or undefined');
        return;
      }
      
      if (!customer.id && !customer.idcust) {
        console.error('Customer ID is missing:', customer);
        return;
      }
      
      dispatch(selectCustomer(customer));
      
      // Get user's current GPS coordinates
      let coordinates = { lat: 0, lng: 0 };
      
      try {
        const position = await new Promise<GeolocationPosition>((resolve, reject) => {
          if (!navigator.geolocation) {
            reject(new Error('Geolocation not supported'));
            return;
          }
          
          navigator.geolocation.getCurrentPosition(
            resolve,
            reject,
            { 
              enableHighAccuracy: true,
              timeout: 10000,
              maximumAge: 60000 
            }
          );
        });
        
        coordinates = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
      } catch (error) {
        console.warn('Failed to get GPS coordinates:', error);
        // Use customer coordinates as fallback
        coordinates = {
          lat: customer.latitude || 0,
          lng: customer.longitude || 0
        };
      }
      
      // Create local visit for active visit tracking
      const currentDateTime = new Date().toLocaleString('en-GB', {
        day: '2-digit',
        month: '2-digit', 
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      });
      
      const localVisit = {
        id: `temp_${Date.now()}`,
        employee_id: '', // Will be filled by the app
        customer_id: customer.id || parseInt(customer.idcust || '0'),
        customer_name: customer.name || customer.NamaPelangganTambak || customer.tambak_name || 'Unknown Customer',
        customer_address: customer.address || '',
        start_time: getWIBTimeISO(),
        status: 'active' as const,
        notes: `Visit for ${customer.name || customer.NamaPelangganTambak || customer.tambak_name || 'Unknown Customer'}. Visit started from Activity Interactions on ${currentDateTime}.`,
        satisfaction_rating: 0,
        latitude: coordinates.lat,
        longitude: coordinates.lng,
        visit_coordinates: coordinates,
        customer_tambak_pelanggan: customer.idcust || customer.id,
        customer_data: customer,
        // IMPROVEMENT: Store reference to original interaction for later updates
        original_interaction_id: interaction.id,
        original_interaction_data: interaction,
        created_at: getWIBTimeISO(),
        updated_at: getWIBTimeISO()
      };
      
      console.log('🎯 Starting visit with coordinates:', coordinates);
      
      // Set active visit in Redux store
      dispatch(setActiveVisit(localVisit));
      
      // Navigate to ActiveVisit screen
      dispatch(navigateTo('visitActive'));
      
      // Close all modals
      setShowCustomerDetails(false);
      setSelectedCustomer(null);
      onClose(); // Close the interaction detail modal
      
      // Haptic feedback
      if (safeHapticNotification) {
        (window as any).Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }
      
      console.log('✅ Visit started successfully');
      
    } catch (error) {
      console.error('❌ Failed to start visit:', error);
      
      // Haptic feedback for error
      if (safeHapticNotification) {
        (window as any).Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    }
  }, [dispatch, onClose]);

  const handlePhoneNumberSelect = useCallback(async (phoneNumber: PhoneNumber, actionType: 'call' | 'whatsapp') => {
    // IMPROVEMENT: Update interaction_type based on action
    const interactionTypeMap = {
      'whatsapp': 'WhatsApp',
      'call': 'Phone Call'
    };
    
    const updatedInteractionType = interactionTypeMap[actionType];
    
    // Update interaction in database with correct type
    if (interaction) {
      try {
        await directusOperations.updateCustomerInteraction(interaction.id, {
          interaction_type: updatedInteractionType
        });
        console.log('✅ Updated interaction_type to:', updatedInteractionType);
      } catch (error) {
        console.warn('⚠️ Failed to update interaction_type:', error);
      }
    }
    
    // Execute the communication action AFTER updating type
    if (actionType === 'whatsapp') {
      const whatsappUrl = `https://wa.me/${phoneNumber.number.replace(/[^\d]/g, '')}`;
      window.open(whatsappUrl, '_blank');
    } else {
      window.location.href = `tel:${phoneNumber.number}`;
    }
    
    // THEN store the action and show notes modal AFTER the communication
    setPendingAction({
      type: actionType,
      phoneNumber: phoneNumber
    });
    setShowPhoneModal(false);
    setSelectedAction(null);
    setShowNotesModal(true);
    
    // Safe haptic feedback for selection
    safeHapticImpact('light');
  }, [interaction]);

  // Handle action selection with immediate SweetAlert
  const handleActionSelect = useCallback(async (actionType: 'whatsapp' | 'call', notes: string, files?: File[]): Promise<boolean> => {
    if (!interaction) return false;

    // DEBUGGING: Log file upload attempt
    console.log('📎 handleActionSelect called with:', {
      actionType,
      notesLength: notes.length,
      filesCount: files?.length || 0,
      files: files?.map(f => ({ name: f.name, size: f.size, type: f.type })) || 'no files'
    });
    // Check if customer has multiple phone numbers
    if (phoneNumbers.length === 0) {
      await Swal.fire({
        title: 'No Phone Number',
        text: 'No phone number available for this customer.',
        icon: 'warning',
        confirmButtonText: 'OK'
      });
      return false;
    }

    let selectedPhoneNumber = phoneNumbers[0]; // Default to first number

    // If multiple phone numbers, show selection dialog
    if (phoneNumbers.length > 1) {
      const phoneOptionsHtml = phoneNumbers.map((phone, index) => {
        const formattedNumber = phone.number.replace(/[^\d+]/g, '');
        const displayNumber = formattedNumber.startsWith('+62')
          ? formattedNumber.replace('+62', '+62 ').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
          : formattedNumber;

        return `
          <div class="phone-option" style="margin: 8px 0;">
            <label style="display: flex; align-items: center; cursor: pointer; padding: 12px; border: 2px solid #e5e7eb; border-radius: 8px; transition: all 0.2s;">
              <input type="radio" name="phoneSelection" value="${index}" style="margin-right: 12px; transform: scale(1.2);" ${index === 0 ? 'checked' : ''}>
              <div style="flex: 1; text-align: left;">
                <div style="font-weight: 600; color: #1f2937;">${displayNumber}</div>
                <div style="font-size: 0.875rem; color: #6b7280; margin-top: 2px;">${phone.source.replace('_', ' ')}</div>
              </div>
            </label>
          </div>
        `;
      }).join('');

      const phoneSelectionResult = await Swal.fire({
        title: 'Select Phone Number',
        html: `
          <div style="text-align: left; margin-bottom: 16px;">
            <p style="color: #6b7280; margin-bottom: 16px;">Choose which phone number to contact for <strong>${interaction.customer_name}</strong>:</p>
            <div id="phoneOptions">
              ${phoneOptionsHtml}
            </div>
          </div>
        `,
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Continue',
        cancelButtonText: 'Cancel',
        confirmButtonColor: 'hsl(var(--status-info))',
        cancelButtonColor: 'hsl(var(--status-neutral))',
        didOpen: () => {
          // Add hover effects and interaction
          const phoneOptions = document.querySelectorAll('.phone-option label');
          phoneOptions.forEach((option: any) => {
            option.addEventListener('mouseenter', () => {
              option.style.borderColor = '#3b82f6';
              option.style.backgroundColor = '#eff6ff';
            });
            option.addEventListener('mouseleave', () => {
              const radio = option.querySelector('input[type="radio"]');
              if (!radio.checked) {
                option.style.borderColor = '#e5e7eb';
                option.style.backgroundColor = 'transparent';
              }
            });
            option.addEventListener('click', () => {
              phoneOptions.forEach((opt: any) => {
                opt.style.borderColor = '#e5e7eb';
                opt.style.backgroundColor = 'transparent';
              });
              option.style.borderColor = '#3b82f6';
              option.style.backgroundColor = '#eff6ff';
            });
          });
        }
      });

      if (phoneSelectionResult.isDismissed) {
        return false; // User cancelled phone selection
      }

      // Get selected phone number
      const selectedRadio = document.querySelector('input[name="phoneSelection"]:checked') as HTMLInputElement;
      if (selectedRadio) {
        const selectedIndex = parseInt(selectedRadio.value);
        selectedPhoneNumber = phoneNumbers[selectedIndex];
      }
    }

    // Format selected phone number for display
    const formattedNumber = selectedPhoneNumber.number.replace(/[^\d+]/g, '');
    const displayNumber = formattedNumber.startsWith('+62')
      ? formattedNumber.replace('+62', '+62 ').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
      : formattedNumber;

    // Show SweetAlert confirmation with selected phone number
    const result = await Swal.fire({
      title: `${actionType === 'whatsapp' ? 'WhatsApp' : 'Call'} Customer`,
      html: `
        <div class="text-left space-y-3">
          <div class="bg-primary/10 dark:bg-primary/20 p-4 rounded-lg">
            <h4 class="font-semibold text-primary dark:text-primary mb-2">Customer Details</h4>
            <p><strong>Name:</strong> ${interaction.customer_name}</p>
            <p><strong>Phone:</strong> ${displayNumber}</p>
            <p><strong>Source:</strong> ${selectedPhoneNumber.source.replace('_', ' ')}</p>
            ${phoneNumbers.length > 1 ? `<p><strong>Selected:</strong> ${displayNumber} (${selectedPhoneNumber.source.replace('_', ' ')})</p>` : ''}
          </div>
          <div class="bg-background dark:bg-gray-800 p-4 rounded-lg">
            <h4 class="font-semibold text-gray-800 dark:text-gray-300 mb-2">Choose Action</h4>
            <p class="text-sm text-gray-600 dark:text-gray-400">What would you like to do?</p>
          </div>
        </div>
      `,
      icon: actionType === 'whatsapp' ? 'question' : 'info',
      showCancelButton: true,
      showDenyButton: true,
      confirmButtonText: `📱 ${actionType === 'whatsapp' ? 'Open WhatsApp' : 'Make Call'}`,
      denyButtonText: '✅ Mark as Done Only',
      cancelButtonText: 'Cancel',
      confirmButtonColor: actionType === 'whatsapp' ? 'hsl(var(--service-whatsapp))' : 'hsl(var(--status-info))',
      denyButtonColor: 'hsl(var(--status-success))',
      cancelButtonColor: 'hsl(var(--status-neutral))',
      reverseButtons: true
    });

    let shouldCloseModal = false;

    if (result.isConfirmed) {
      // Execute the communication action
      if (actionType === 'whatsapp') {
        const whatsappUrl = `https://wa.me/${selectedPhoneNumber.number.replace(/[^\d]/g, '')}`;
        window.open(whatsappUrl, '_blank');
      } else if (actionType === 'call') {
        window.location.href = `tel:${selectedPhoneNumber.number}`;
      }
      // Close modal after communication action
      shouldCloseModal = true;
    } else if (result.isDenied) {
      // Just mark as done without opening app
      console.log('✅ Marked as done without opening communication app');
      // Don't close modal - user wants to stay in Add Notes to add more details
      shouldCloseModal = false;
    } else {
      // User cancelled
      return false;
    }

    // Save notes and update interaction
    try {
      // Prepare update data
      const updateData: any = {
        keterangan: notes,
        interaction_type: actionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call'
      };

      // Handle file uploads if any
      let uploadedFileIds: (string | number)[] = [];
      console.log('🔍 DEBUGGING file upload check:', {
        hasFiles: !!files,
        filesLength: files?.length || 0,
        interactionId: interaction.id
      });

      if (files && files.length > 0) {
        console.log(`📎 Starting upload of ${files.length} files for interaction ${interaction.id}:`,
          files.map(f => ({ name: f.name, size: f.size, type: f.type })));

        // Upload files to customerInteractionFile table
        for (const file of files) {
          try {
            const uploadResult = await directusOperations.uploadAndCreateCustomerInteractionFile(file);
            console.log(`✅ Uploaded file ${file.name} to customerInteractionFile:`, uploadResult.interactionFileRecord.id);
            // UPDATED FIX: customerInteractionFile table uses UUID primary keys, not integers
            const customerInteractionFileId = uploadResult.interactionFileRecord.id;
            if (customerInteractionFileId && (typeof customerInteractionFileId === 'string' || typeof customerInteractionFileId === 'number')) {
              uploadedFileIds.push(customerInteractionFileId);
              console.log(`📎 Added customerInteractionFile ID ${customerInteractionFileId} (${typeof customerInteractionFileId}) to file list`);
            } else {
              console.error(`❌ Invalid customerInteractionFile ID:`, typeof customerInteractionFileId, customerInteractionFileId);
            }
          } catch (fileError) {
            console.error(`❌ Failed to upload file ${file.name}:`, fileError);
            // Show error notification but don't fail the whole operation
            if (safeHapticImpact) {
              window.Telegram.WebApp.HapticFeedback.notificationOccurred('warning');
            }
          }
        }
      }

      // Add uploaded file IDs to update data if any files were uploaded
      if (uploadedFileIds.length > 0) {
        updateData.file = uploadedFileIds;
        console.log(`📎 Linking ${uploadedFileIds.length} customerInteractionFile IDs to interaction:`, uploadedFileIds);
      }

      // Update the interaction with keterangan, type, and file IDs
      await directusOperations.updateCustomerInteraction(interaction.id, updateData);

      // Check if files were uploaded but couldn't be linked (fallback scenario)
      if (uploadedFileIds.length > 0) {
        console.log(`✅ Successfully saved notes and uploaded ${uploadedFileIds.length} files`);
      }

      // Update local interaction state with complete data including file attachments
      const updatedInteraction = {
        ...interaction,
        keterangan: notes,
        interaction_type: actionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call',
        // Include file attachments if they were uploaded (ensure all are integers)
        file: uploadedFileIds.length > 0 ?
          [...(interaction.file || []).filter(id => typeof id === 'number'), ...uploadedFileIds] :
          (interaction.file || []).filter(id => typeof id === 'number')
      };
      onUpdate(updatedInteraction);

      // Haptic feedback
      if (safeHapticImpact) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }

      // Show success notification
      console.log(`✅ Successfully saved interaction with ${uploadedFileIds.length} files:`, {
        interactionId: interaction.id,
        keterangan: notes,
        interaction_type: actionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call',
        fileIds: uploadedFileIds,
        fileIdTypes: uploadedFileIds.map(id => typeof id)
      });

      return shouldCloseModal;
    } catch (error) {
      console.error('Failed to save interaction:', error);
      if (safeHapticImpact) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
      return false;
    }
  }, [interaction, phoneNumbers, onUpdate]);

  // Save notes only (without action popup) - for "Save & Continue" without action selection
  const saveNotesOnly = useCallback(async (notes: string, files?: File[], markedActionType?: 'whatsapp' | 'call' | null, progress?: ProgressStatus) => {
    if (!interaction) return;

    // DEBUGGING: Log saveNotesOnly call
    console.log('💾 saveNotesOnly called with:', {
      notesLength: notes.length,
      filesCount: files?.length || 0,
      files: files?.map(f => ({ name: f.name, size: f.size, type: f.type })) || 'no files',
      interactionId: interaction.id,
      markedActionType: markedActionType
    });

    try {
      // Prepare update data for notes
      const updateData: any = {
        keterangan: notes
      };

      // Set interaction_type if markedActionType is provided
      if (markedActionType) {
        const interactionType = markedActionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call';
        updateData.interaction_type = interactionType;
        console.log('📞 Setting interaction_type:', {
          markedActionType,
          interactionType,
          interactionId: interaction.id
        });
      }

      // Add progress status if provided
      if (progress) {
        updateData.Progress = progress;
        console.log('📊 Adding progress status:', {
          progress,
          interactionId: interaction.id
        });
      }

      // AUTO-COMPLETE: Mark interaction as done after Save & Continue
      updateData.done = true;
      updateData.completed_date = getWIBTimeForDirectus();
      console.log('✅ Auto-completing interaction:', {
        interactionId: interaction.id,
        doneStatus: true,
        completedDate: updateData.completed_date
      });

      // Handle file uploads if any
      let uploadedFileIds: (string | number)[] = [];
      console.log('🔍 DEBUGGING saveNotesOnly file upload check:', {
        hasFiles: !!files,
        filesLength: files?.length || 0,
        interactionId: interaction.id
      });

      if (files && files.length > 0) {
        console.log(`📎 Starting upload of ${files.length} files for interaction ${interaction.id}:`,
          files.map(f => ({ name: f.name, size: f.size, type: f.type })));

        // Upload files to customerInteractionFile table
        for (const file of files) {
          try {
            const uploadResult = await directusOperations.uploadAndCreateCustomerInteractionFile(file);
            console.log(`✅ Uploaded file ${file.name} to customerInteractionFile:`, uploadResult.interactionFileRecord.id);
            // UPDATED FIX: customerInteractionFile table uses UUID primary keys, not integers
            const customerInteractionFileId = uploadResult.interactionFileRecord.id;
            if (customerInteractionFileId && (typeof customerInteractionFileId === 'string' || typeof customerInteractionFileId === 'number')) {
              uploadedFileIds.push(customerInteractionFileId);
              console.log(`📎 Added customerInteractionFile ID ${customerInteractionFileId} (${typeof customerInteractionFileId}) to file list`);
            } else {
              console.error(`❌ Invalid customerInteractionFile ID:`, typeof customerInteractionFileId, customerInteractionFileId);
            }
          } catch (fileError) {
            console.error(`❌ Failed to upload file ${file.name}:`, fileError);
            if (safeHapticImpact) {
              window.Telegram.WebApp.HapticFeedback.notificationOccurred('warning');
            }
          }
        }
      }

      // Add uploaded file IDs to update data if any files were uploaded
      if (uploadedFileIds.length > 0) {
        updateData.file = uploadedFileIds;
        console.log(`📎 Linking ${uploadedFileIds.length} customerInteractionFile IDs to interaction:`, uploadedFileIds);
      }

      // Update the interaction with keterangan and file IDs
      await directusOperations.updateCustomerInteraction(interaction.id, updateData);

      // Check if files were uploaded but couldn't be linked (fallback scenario)
      if (uploadedFileIds.length > 0) {
        console.log(`✅ Successfully saved notes and uploaded ${uploadedFileIds.length} files`);
      }

      // Update local interaction state with complete data including file attachments, interaction_type, and completion status
      const updatedInteraction = {
        ...interaction,
        keterangan: notes,
        // Include file attachments if they were uploaded (ensure all are integers)
        file: uploadedFileIds.length > 0 ?
          [...(interaction.file || []).filter(id => typeof id === 'number'), ...uploadedFileIds] :
          (interaction.file || []).filter(id => typeof id === 'number'),
        // Update interaction_type if markedActionType was provided
        ...(markedActionType && {
          interaction_type: markedActionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call'
        }),
        // AUTO-COMPLETE: Update local state with completion data
        done: true,
        completed_date: updateData.completed_date,
        status: 'completed' as const
      };
      onUpdate(updatedInteraction);

      // Haptic feedback for success
      if (safeHapticImpact) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }

      console.log(`✅ Notes saved successfully without action popup. ${uploadedFileIds.length} files uploaded.`);

      // Show success notification with completion status
      const successText = [
        'Notes have been saved successfully',
        uploadedFileIds.length > 0 ? `with ${uploadedFileIds.length} file(s)` : '',
        markedActionType ? `Interaction type set to ${markedActionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call'}.` : '',
        '✅ Interaction marked as completed!'
      ].filter(Boolean).join(' ');

      await Swal.fire({
        icon: 'success',
        title: 'Completed!',
        text: successText,
        confirmButtonText: 'Great!',
        confirmButtonColor: 'hsl(var(--primary))',
        timer: 3500,
        timerProgressBar: true
      });

      // Call onComplete callback to notify parent component
      onComplete(updatedInteraction);

      // Force UI refresh by triggering a small delay to ensure state update
      setTimeout(() => {
        console.log('🔄 UI state should now reflect the updated keterangan and file attachments');
      }, 100);

    } catch (error) {
      console.error('Failed to save notes:', error);
      if (safeHapticImpact) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }

      // Show error notification with SweetAlert
      await Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: 'Failed to save notes. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: 'hsl(var(--destructive))'
      });

      throw error; // Re-throw to handle in calling function
    }
  }, [interaction, onUpdate, onComplete]);

  // Handle notes save - simplified to directly save without popup
  const handleNotesSave = useCallback(async (notes: string, actionType?: 'whatsapp' | 'call', files?: File[], markedActionType?: 'whatsapp' | 'call' | null, progress?: ProgressStatus) => {
    if (!interaction) return;

    // Debug log for progress parameter
    console.log('📊 handleNotesSave called with progress:', {
      progress,
      hasProgress: !!progress,
      notesLength: notes.length,
      actionType,
      filesCount: files?.length || 0,
      markedActionType,
      interactionId: interaction.id
    });

    // If there's a markedActionType, we need to save with the proper interaction_type
    if (markedActionType) {
      console.log('📞 Saving notes with marked action type:', {
        markedActionType,
        willSetInteractionType: markedActionType === 'whatsapp' ? 'WhatsApp' : 'Phone Call',
        notes: notes.substring(0, 50) + '...',
        filesCount: files?.length || 0,
        interactionId: interaction.id
      });

      // Call saveNotesOnly with specific interaction_type
      await saveNotesOnly(notes, files, markedActionType, progress);
    } else {
      // Always save notes and files directly to Directus using saveNotesOnly
      // This already includes SweetAlert success/error notifications
      await saveNotesOnly(notes, files, undefined, progress);
    }
  }, [interaction, saveNotesOnly]);

  // Handle notes modal close
  const handleNotesClose = useCallback(() => {
    setShowNotesModal(false);
    setPendingAction(null);
  }, []);

  // Handle direct contact from notes modal (new feature)
  const handleDirectContact = useCallback(async (actionType: 'whatsapp' | 'call', phoneNumber: string, shouldMark: boolean) => {
    console.log('🎯 Direct contact initiated:', {
      actionType,
      phoneNumber,
      shouldMark,
      customerName: interaction?.customer_name
    });

    try {
      if (shouldMark) {
        // Mark only - just close the notes modal and show success
        await Swal.fire({
          icon: 'info',
          title: 'Number Marked',
          text: `Phone number ${phoneNumber} has been marked for ${interaction?.customer_name}.`,
          confirmButtonText: 'OK',
          confirmButtonColor: 'hsl(var(--primary))',
          timer: 2000,
          timerProgressBar: true
        });
        return;
      }

      // Contact action - redirect to WhatsApp or Call
      if (actionType === 'whatsapp') {
        // Clean phone number for WhatsApp (remove any formatting, keep + if present)
        const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
        const whatsappNumber = cleanNumber.startsWith('+') ? cleanNumber : `+62${cleanNumber.replace(/^0/, '')}`;
        const whatsappUrl = `https://wa.me/${whatsappNumber.replace('+', '')}`;

        console.log('📱 Opening WhatsApp:', {
          originalNumber: phoneNumber,
          cleanedNumber: cleanNumber,
          whatsappNumber: whatsappNumber,
          url: whatsappUrl
        });

        // Open WhatsApp in new tab
        window.open(whatsappUrl, '_blank');

        // Haptic feedback
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
        }

        // Show success message
        await Swal.fire({
          icon: 'success',
          title: 'WhatsApp Opened',
          text: `Opening WhatsApp chat with ${interaction?.customer_name}`,
          confirmButtonText: 'Great!',
          confirmButtonColor: '#25D366',
          timer: 2000,
          timerProgressBar: true
        });

      } else if (actionType === 'call') {
        // Create tel: link for calling
        const cleanNumber = phoneNumber.replace(/[^\d+]/g, '');
        const telUrl = `tel:${cleanNumber}`;

        console.log('📞 Initiating call:', {
          originalNumber: phoneNumber,
          cleanedNumber: cleanNumber,
          url: telUrl
        });

        // Open phone dialer
        window.location.href = telUrl;

        // Haptic feedback
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
        }

        // Show success message
        await Swal.fire({
          icon: 'success',
          title: 'Call Initiated',
          text: `Initiating call to ${interaction?.customer_name}`,
          confirmButtonText: 'OK',
          confirmButtonColor: 'hsl(var(--primary))',
          timer: 2000,
          timerProgressBar: true
        });
      }

      // Close the notes modal after contact action
      setShowNotesModal(false);
      setPendingAction(null);

    } catch (error) {
      console.error('Failed to handle direct contact:', error);

      await Swal.fire({
        icon: 'error',
        title: 'Contact Failed',
        text: `Failed to contact ${interaction?.customer_name}. Please try again.`,
        confirmButtonText: 'OK',
        confirmButtonColor: 'hsl(var(--destructive))'
      });
    }
  }, [interaction]);

  // Notes editing handlers
  const handleEditNotes = useCallback(() => {
    setIsEditingNotes(true);
  }, []);

  const handleCancelEditNotes = useCallback(() => {
    setIsEditingNotes(false);
    setEditedNotes(interaction?.keterangan || '');
  }, [interaction?.keterangan]);

  const handleSaveNotes = useCallback(async () => {
    if (!interaction) return;

    setIsSavingNotes(true);
    try {
      // Update the interaction with new notes
      await directusOperations.updateCustomerInteraction(interaction.id, {
        keterangan: editedNotes
      });

      // Update local interaction state
      const updatedInteraction = {
        ...interaction,
        keterangan: editedNotes
      };
      onUpdate(updatedInteraction);

      setIsEditingNotes(false);

      // Haptic feedback for success
      if (safeHapticImpact) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
      }

    } catch (error) {
      console.error('Failed to save notes:', error);
      
      // Haptic feedback for error
      if (safeHapticImpact) {
        window.Telegram.WebApp.HapticFeedback.notificationOccurred('error');
      }
    } finally {
      setIsSavingNotes(false);
    }
  }, [interaction, editedNotes, onUpdate]);

  const formatPhoneNumber = useCallback((number: string) => {
    // Clean and format phone number for display
    const cleaned = number.replace(/[^\d+]/g, '');
    if (cleaned.startsWith('+62')) {
      return cleaned.replace('+62', '+62 ').replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3');
    }
    return cleaned;
  }, []);

  const getPhoneTypeIcon = useCallback((type: string) => {
    switch (type) {
      case 'whatsapp':
        return <MessageCircle className="w-4 h-4 text-green-500" />;
      default:
        return <Phone className="w-4 h-4 text-primary" />;
    }
  }, []);

  // Append details handler (for completed interactions within 7-day window)
  const handleAppendDetails = useCallback(async (content: string, files?: File[]) => {
    if (!interaction) return;

    try {
      // Format the appended content with markdown separator and timestamp
      const currentKeterangan = interaction.keterangan || '';
      const updatedKeterangan = formatAppendedContent(currentKeterangan, content, getWIBTimeForDirectus());

      // Handle file uploads if provided
      let uploadedFileIds: (string | number)[] = [];
      if (files && files.length > 0) {
        console.log(`📎 Uploading ${files.length} file(s) for appended details...`);

        // Upload files using customerInteractionFile junction table (matching existing pattern)
        for (const file of files) {
          try {
            const uploadResult = await directusOperations.uploadAndCreateCustomerInteractionFile(file);
            console.log(`✅ Uploaded file ${file.name} to customerInteractionFile:`, uploadResult.interactionFileRecord.id);

            const customerInteractionFileId = uploadResult.interactionFileRecord.id;
            if (customerInteractionFileId && (typeof customerInteractionFileId === 'string' || typeof customerInteractionFileId === 'number')) {
              uploadedFileIds.push(customerInteractionFileId);
              console.log(`📎 Added customerInteractionFile ID ${customerInteractionFileId} (${typeof customerInteractionFileId}) to file list`);
            } else {
              console.error(`❌ Invalid customerInteractionFile ID:`, typeof customerInteractionFileId, customerInteractionFileId);
            }
          } catch (fileError) {
            console.error(`❌ Failed to upload file ${file.name}:`, fileError);

            // Show error notification for individual file but continue with others
            if (safeHapticImpact) {
              window.Telegram.WebApp.HapticFeedback.notificationOccurred('warning');
            }
          }
        }

        // Show summary if some uploads failed
        const successCount = uploadedFileIds.length;
        const failCount = files.length - successCount;
        if (failCount > 0 && successCount === 0) {
          // All files failed
          await Swal.fire({
            icon: 'warning',
            title: 'File Upload Failed',
            text: 'Files could not be uploaded, but text will be saved.',
            confirmButtonText: 'Continue',
            confirmButtonColor: 'hsl(var(--warning))'
          });
        } else if (failCount > 0) {
          // Some files failed
          await Swal.fire({
            icon: 'warning',
            title: 'Partial Upload Success',
            text: `${successCount} of ${files.length} files uploaded successfully. ${failCount} failed.`,
            confirmButtonText: 'Continue',
            confirmButtonColor: 'hsl(var(--warning))'
          });
        }
      }

      // Update the interaction with appended content and file IDs
      const updateData: any = {
        keterangan: updatedKeterangan
      };

      // Merge new file IDs with existing files (if any)
      if (uploadedFileIds.length > 0) {
        const existingFileIds = (interaction.file || []).filter((id: any) =>
          typeof id === 'string' || typeof id === 'number'
        );
        updateData.file = [...existingFileIds, ...uploadedFileIds];
      }

      await directusOperations.updateCustomerInteraction(interaction.id, updateData);

      // Update local interaction state
      const updatedInteraction = {
        ...interaction,
        keterangan: updatedKeterangan,
        file: updateData.file || interaction.file || []
      };
      onUpdate(updatedInteraction);

      // Haptic feedback for success
      if (safeHapticNotification) {
        safeHapticNotification('success');
      }

      // Show success message
      const successText = uploadedFileIds.length > 0
        ? `Additional details saved with ${uploadedFileIds.length} file(s)`
        : 'Additional details saved successfully';

      await Swal.fire({
        icon: 'success',
        title: 'Details Added',
        text: successText,
        confirmButtonText: 'Great!',
        confirmButtonColor: 'hsl(var(--primary))',
        timer: 3000,
        timerProgressBar: true
      });

      console.log('✅ Appended details saved successfully');

    } catch (error) {
      console.error('Failed to append details:', error);

      // Haptic feedback for error
      if (safeHapticNotification) {
        safeHapticNotification('error');
      }

      await Swal.fire({
        icon: 'error',
        title: 'Save Failed',
        text: 'Failed to save additional details. Please try again.',
        confirmButtonText: 'OK',
        confirmButtonColor: 'hsl(var(--destructive))'
      });

      throw error;
    }
  }, [interaction, onUpdate]);


  if (!isOpen || !interaction) return null;

  // Determine interaction status for conditional rendering
  const interactionStatus = determineInteractionStatus(interaction.date_created, interaction.done);
  const isOverdue = interactionStatus.status === 'overdue';

  return (
    <div className="fixed inset-0 bg-black bg-black/50 flex items-end z-[60]">
      <div className="bg-card w-full max-h-[95vh] rounded-t-3xl flex flex-col border border-border">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border bg-card shadow-sm flex-shrink-0">
          <div className="flex-1">
            <div className="flex items-center space-x-3 mb-2">
              <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center text-primary font-bold">
                {interaction.customer_name.charAt(0)}
              </div>
              <div>
                <h2 className="text-lg font-bold truncate">{interaction.customer_name}</h2>
                <p className="text-sm text-muted-foreground">{interaction.No_pelanggan}</p>
              </div>
            </div>
            <Badge variant={getStatusVariant(interactionStatus.status)} className="text-sm font-medium">
              {interactionStatus.label}
            </Badge>
          </div>
          <div className="flex flex-col items-end space-y-2">
            <div className="flex items-center space-x-2">
              
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted/50 rounded-full transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>
        </div>

        {/* Scrollable Content */}
        <div className="flex-1 min-h-0 max-h-full overflow-y-auto">
          <div className="p-6 space-y-6">
          {/* Interaction Details */}
          <div className="space-y-4">
            {/* Subject section hidden */}

            {/* Interaction Type and DateTime */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="block text-sm font-medium text-muted-foreground">
                  {t('interactions.interaction_detail_modal.type')}
                </label>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  {getInteractionTypeIcon(interaction.interaction_type)}
                  <span className="font-medium">
                    {(() => {
                      switch (interaction.interaction_type) {
                        case 'WhatsApp':
                          return language === 'id' ? t('interactions.interaction_detail_modal.indonesian_whatsapp') : 'WhatsApp';
                        case 'Phone Call':
                          return language === 'id' ? t('interactions.interaction_detail_modal.indonesian_call') : 'Phone Call';
                        case 'Visit/Meeting':
                          return language === 'id' ? t('interactions.interaction_detail_modal.indonesian_visit') : 'Visit/Meeting';
                        default:
                          return interaction.interaction_type;
                      }
                    })()}
                  </span>
                </div>
              </div>
              <div className="space-y-2">
                <label className="block text-sm font-medium text-muted-foreground">
                  {(() => {
                    switch (interactionStatus.status) {
                      case 'completed':
                        return t('interactions.interaction_detail_modal.completed');
                      case 'cancel':
                        return t('interactions.interaction_detail_modal.cancelled');
                      case 'overdue':
                        return t('interactions.interaction_detail_modal.overdue');
                      case 'pending':
                      default:
                        return t('interactions.interaction_detail_modal.scheduled');
                    }
                  })()}
                </label>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm">
                    {(() => {
                      if (interaction.completed_date) {
                        return formatDateTime(interaction.completed_date);
                      }
                      if (interaction.scheduled_date) {
                        return formatDateTime(interaction.scheduled_date);
                      }
                      return formatDateTime(interaction.date_created);
                    })()}
                  </span>
                </div>
              </div>
            </div>

            {/* Priority - Hidden */}
            {/* {isEditing ? (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-muted-foreground">{t('interactions.priority')}</label>
                <div className="flex space-x-3">
                  {(['low', 'medium', 'high'] as const).map((priority) => (
                    <button
                      key={priority}
                      onClick={() => setEditedData(prev => ({ ...prev, priority }))}
                      className={`px-4 py-2 rounded-lg border transition-colors ${
                        editedData.priority === priority
                          ? 'border-primary bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary'
                          : 'border-border text-muted-foreground hover:border-muted-foreground'
                      }`}
                    >
                      <span className="text-sm font-medium capitalize">{priority}</span>
                      <span className="ml-1">
                        {priority === 'high' ? '🔴' : priority === 'medium' ? '🟡' : '🟢'}
                      </span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-muted-foreground">{t('interactions.priority')}</label>
                <div className="flex items-center space-x-2">
                  <AlertTriangle className="w-4 h-4 text-muted-foreground" />
                  <span className="capitalize font-medium text-muted-foreground">{interaction.priority}</span>
                  <span>
                    {interaction.priority === 'high' ? '🔴' : interaction.priority === 'medium' ? '🟡' : '🟢'}
                  </span>
                </div>
              </div>
            )} */}

            {/* Notes */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-muted-foreground">
                  {t('interactions.interaction_detail_modal.notes_details')}
                </label>
                {!isEditingNotes && interactionStatus.status !== 'completed' && interactionStatus.status !== 'overdue' && (
                  <button
                    onClick={handleEditNotes}
                    className="flex items-center space-x-1 px-3 py-1 text-sm text-primary dark:text-primary hover:text-primary/80 dark:hover:text-primary/80 hover:bg-primary/10 dark:hover:bg-primary/20 rounded-md transition-colors"
                  >
                    <Edit3 className="w-4 h-4" />
                    <span>{t('interactions.interaction_detail_modal.edit')}</span>
                  </button>
                )}
              </div>
              
              {isEditingNotes ? (
                <div className="space-y-3">
                  <RichTextEditor
                    value={editedNotes}
                    onChange={setEditedNotes}
                    placeholder={t('interactions.interaction_detail_modal.placeholder_notes')}
                    className="border border-border rounded-lg"
                    minHeight="120px"
                  />
                  <div className="flex space-x-2">
                    <button
                      onClick={handleSaveNotes}
                      disabled={isSavingNotes}
                      className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50"
                    >
                      <Save className="w-4 h-4" />
                      <span>{isSavingNotes 
                        ? t('interactions.interaction_detail_modal.saving') 
                        : t('interactions.interaction_detail_modal.save')
                      }</span>
                    </button>
                    <button
                      onClick={handleCancelEditNotes}
                      disabled={isSavingNotes}
                      className="flex items-center space-x-2 px-4 py-2 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors disabled:opacity-50"
                    >
                      <XCircle className="w-4 h-4" />
                      <span>{t('interactions.interaction_detail_modal.cancel')}</span>
                    </button>
                  </div>
                </div>
              ) : (
                <div className="bg-muted/50 rounded-lg p-4">
                  {(() => {
                    const notes = interaction.keterangan || '';
                    const notesLines = notes.split('\n').filter(line => line.trim());
                    const displayLines = notesLines.slice(-6); // Last 6 lines
                    const hasMoreLines = notesLines.length > 6;
                    const hasAnyNotes = notesLines.length > 0;

                    return (
                      <>
                        <div
                          className="text-muted-foreground prose prose-sm max-w-none"
                          dangerouslySetInnerHTML={{
                            __html: displayLines.length > 0
                              ? displayLines.join('\n')
                              : `<em>${t('interactions.interaction_detail_modal.no_notes')}</em>`
                          }}
                        />
                        {hasAnyNotes && (
                          <button
                            onClick={() => setShowNotesDetailModal(true)}
                            className="mt-3 text-sm text-primary hover:text-primary/80 dark:hover:text-primary/70 font-medium flex items-center space-x-1 transition-colors"
                          >
                            <span>{hasMoreLines ? `View all ${notesLines.length} lines` : 'View full notes'}</span>
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                          </button>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* Duration (if completed) */}
            {interaction.status === 'completed' && interaction.duration_minutes && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-muted-foreground">
                  {t('interactions.interaction_detail_modal.duration')}
                </label>
                <div className="flex items-center space-x-2 text-muted-foreground">
                  <Clock className="w-4 h-4" />
                  <span>{interaction.duration_minutes} {t('interactions.interaction_detail_modal.minutes')}</span>
                </div>
              </div>
            )}

            {/* File Attachments */}
            {interaction.file && Array.isArray(interaction.file) && interaction.file.length > 0 && (
              <div className="space-y-2">
                <label className="block text-sm font-medium text-muted-foreground">
                  {t('interactions.interaction_detail_modal.file_attachments')}
                </label>
                <div className="bg-muted/50 rounded-lg p-4">
                  <div className="flex items-center space-x-2 text-muted-foreground">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">
                      {interaction.file.length} {t('interactions.interaction_detail_modal.files_attached', { count: interaction.file.length })}
                    </span>
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">
                    {t('interactions.interaction_detail_modal.file_ids')} {interaction.file.join(', ')}
                  </div>
                </div>
              </div>
            )}

            {/* Follow-up */}
            {interaction.follow_up_required && (
              <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-lg p-4">
                <div className="flex items-center space-x-2 text-orange-800 dark:text-orange-300">
                  <Clock className="w-5 h-5" />
                  <span className="font-medium">
                    {t('interactions.interaction_detail_modal.follow_up_required')}
                  </span>
                </div>
                {interaction.follow_up_date && (
                  <p className="text-sm text-orange-700 mt-1">
                    {t('interactions.interaction_detail_modal.scheduled_for')} {formatDateTime(interaction.follow_up_date)}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Quick Contact Actions - Hidden for overdue and completed interactions */}
          {!isOverdue && interactionStatus.status !== 'completed' && (
            <div className="bg-muted/50 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-semibold text-muted-foreground">
                  {t('interactions.interaction_detail_modal.quick_contact')}
                </h3>
                {phoneLoading && (
                  <div className="text-xs text-muted-foreground">
                    {t('interactions.interaction_detail_modal.loading_numbers')}
                  </div>
                )}
              </div>

              {/* Phone Numbers Info */}
              {!phoneLoading && phoneNumbers.length > 0 && (
                <div className="mb-3 p-2 bg-card rounded-lg border border-border">
                  <div className="text-xs text-muted-foreground mb-1">
                    {t('interactions.interaction_detail_modal.available_numbers')}
                  </div>
                  <div className="space-y-1">
                    {phoneNumbers.slice(0, 2).map((phone, index) => (
                      <div key={index} className="flex items-center space-x-2 text-sm">
                        {getPhoneTypeIcon(phone.type)}
                        <span className="font-mono text-muted-foreground">
                          {formatPhoneNumber(phone.number)}
                        </span>
                        <span className="text-xs text-muted-foreground capitalize">
                          ({phone.source.replace('_', ' ')})
                        </span>
                      </div>
                    ))}
                    {phoneNumbers.length > 2 && (
                      <div className="text-xs text-muted-foreground">
                        +{phoneNumbers.length - 2} more numbers
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setShowNotesModal(true)}
                  disabled={phoneLoading}
                  className={`flex flex-col items-center space-y-2 p-3 rounded-lg transition-colors ${
                    phoneNumbers.length > 0
                      ? 'bg-gradient-to-r from-green-100 to-blue-100 dark:from-green-900/30 dark:to-blue-900/30 text-gray-800 dark:text-gray-300 hover:from-green-200 hover:to-blue-200 dark:hover:from-green-900/50 dark:hover:to-blue-900/50'
                      : 'bg-muted/50 text-muted-foreground'
                  } disabled:opacity-50`}
                >
                  <div className="flex items-center space-x-1">
                    <MessageCircle className="w-5 h-5 text-green-600" />
                    <span className="text-xs">/</span>
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <span className="text-xs font-medium">
                    {t('interactions.interaction_detail_modal.whatsapp_call')}
                  </span>
                  {phoneNumbers.length > 0 && (
                    <span className="text-xs text-gray-600">
                      {t('interactions.interaction_detail_modal.numbers_count', { count: phoneNumbers.length })}
                    </span>
                  )}
                </button>
                
                <button
                  onClick={() => handleContactAction('visit')}
                  className="flex flex-col items-center space-y-2 p-3 bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-300 rounded-lg hover:bg-purple-200 dark:hover:bg-purple-900/50 transition-colors"
                >
                  <MapPin className="w-6 h-6" />
                  <span className="text-xs font-medium">
                    {t('interactions.interaction_detail_modal.visit')}
                  </span>
                </button>
              </div>

              {!phoneLoading && phoneNumbers.length === 0 && !phoneError && (
                <div className="mt-2 text-xs text-muted-foreground text-center">
                  {t('interactions.interaction_detail_modal.no_phone_numbers')}
                </div>
              )}

              {phoneError && (
                <div className="mt-2 text-xs text-red-500 text-center">
                  {phoneError}
                </div>
              )}
            </div>
          )}

          {/* Status-specific notices */}
          {/* Overdue Notice - Show for overdue interactions that aren't completed */}
          {isOverdue && interactionStatus.status !== 'completed' && (
            <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-xl p-4">
              <div className="flex items-center space-x-2 text-orange-800 dark:text-orange-300 mb-2">
                <AlertTriangle className="w-5 h-5" />
                <h3 className="font-semibold">
                  {t('interactions.interaction_detail_modal.overdue_interaction')}
                </h3>
              </div>
              <p className="text-sm text-orange-700">
                {t('interactions.interaction_detail_modal.overdue_message')}
              </p>
            </div>
          )}
          
          {/* Completed Notice - Show for completed interactions */}
          {interactionStatus.status === 'completed' && (
            <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-700 rounded-xl p-4">
              <div className="flex items-center space-x-2 text-green-800 dark:text-green-300 mb-2">
                <CheckCircle className="w-5 h-5" />
                <h3 className="font-semibold">
                  {t('interactions.interaction_detail_modal.interaction_completed')}
                </h3>
              </div>
              <p className="text-sm text-green-700">
                {t('interactions.interaction_detail_modal.completed_message')}
              </p>
            </div>
          )}

          </div>
        </div>

        {/* Fixed Action Buttons */}
        <div className="flex-shrink-0 p-6 border-t border-border bg-background">
          <div className="space-y-3">
            {/* Show "Add Details" button for completed interactions within 7-day window */}
            {interactionStatus.status === 'completed' ? (
              <>
                {isWithinAppendWindow(interaction.completed_date) ? (
                  <div className="space-y-3">
                    {/* Info banner about append window */}
                    <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-lg p-3">
                      <p className="text-xs text-primary dark:text-primary">
                        <strong>Add Details:</strong> You can append additional information to this completed interaction for{' '}
                        {getRemainingAppendDays(interaction.completed_date)} more day(s).
                      </p>
                    </div>

                    {/* Add Details Button */}
                    <button
                      onClick={() => setShowAppendModal(true)}
                      className="w-full px-6 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                    >
                      <FilePlus className="w-5 h-5" />
                      <span>Add Additional Details</span>
                    </button>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <p className="text-sm text-muted-foreground">
                      {t('interactions.interaction_detail_modal.no_actions_completed')}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Show Add Additional Details button for overdue interactions */}
                {isOverdue ? (
                  <div className="space-y-3">
                    {/* Info banner about append window for overdue */}
                    {isWithinAppendWindow(interaction.date_created) ? (
                      <>
                        <div className="bg-orange-50 dark:bg-orange-900/30 border border-orange-200 dark:border-orange-700 rounded-lg p-3">
                          <p className="text-xs text-orange-800 dark:text-orange-300">
                            <strong>Add Details:</strong> You can append additional information for{' '}
                            {getRemainingAppendDays(interaction.date_created)} more day(s).
                          </p>
                        </div>
                        <button
                          onClick={() => setShowAppendModal(true)}
                          className="w-full px-6 py-3 bg-orange-500 text-white rounded-lg font-medium hover:bg-orange-600 transition-colors flex items-center justify-center space-x-2"
                        >
                          <FilePlus className="w-5 h-5" />
                          <span>Add Additional Details</span>
                        </button>
                      </>
                    ) : (
                      <div className="text-center py-4">
                        <p className="text-sm text-muted-foreground">
                          The 7-day window to add details has expired.
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Show normal actions for non-overdue, non-completed interactions
                  <button
                    onClick={() => {
                      // Pre-fill type from source interaction, normalize lowercase variants,
                      // and default to WhatsApp if blank/unknown.
                      const raw = (interaction?.interaction_type || '').trim().toLowerCase();
                      const normalized: 'WhatsApp' | 'Phone Call' | 'Visit/Meeting' =
                        raw === 'phone call' || raw === 'phonecall' ? 'Phone Call'
                        : raw === 'visit/meeting' ? 'Visit/Meeting'
                        : 'WhatsApp';
                      setRescheduleType(normalized);
                      setShowRescheduleDialog(true);
                    }}
                    className="w-full px-6 py-3 bg-primary/10 dark:bg-primary/20 text-primary dark:text-primary rounded-lg font-medium hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors flex items-center justify-center space-x-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>{t('interactions.interaction_detail_modal.reschedule')}</span>
                  </button>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Complete Interaction Dialog */}
      {showCompleteDialog && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-60 p-4">
          <div className="bg-card rounded-xl p-6 w-full max-w-md border border-border">
            <h3 className="text-lg font-bold text-muted-foreground mb-4">
              {t('interactions.interaction_detail_modal.complete_interaction')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('interactions.interaction_detail_modal.completion_notes')}
                </label>
                <textarea
                  value={completionNotes}
                  onChange={(e) => setCompletionNotes(e.target.value)}
                  rows={3}
                  placeholder={t('interactions.interaction_detail_modal.completion_placeholder')}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring resize-none bg-background text-foreground"
                />
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowCompleteDialog(false)}
                  className="flex-1 px-4 py-2 border border-border text-muted-foreground rounded-lg font-medium hover:bg-muted/50 transition-colors"
                >
                  {t('interactions.interaction_detail_modal.cancel')}
                </button>
                <button
                  onClick={handleComplete}
                  className="flex-1 px-4 py-2 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors"
                >
                  {t('interactions.interaction_detail_modal.complete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Dialog */}
      {showRescheduleDialog && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-60 p-4">
          <div className="bg-card rounded-xl p-6 w-full max-w-md border border-border">
            <h3 className="text-lg font-bold text-muted-foreground mb-4">
              {t('interactions.interaction_detail_modal.reschedule_interaction')}
            </h3>
            <div className="bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-lg p-3 mb-4">
              <p className="text-sm text-primary dark:text-primary">
                {t('interactions.interaction_detail_modal.reschedule_description')}
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('interactions.interaction_detail_modal.new_date')}
                </label>
                <input
                  type="date"
                  value={rescheduleDate}
                  onChange={(e) => setRescheduleDate(e.target.value)}
                  min={getWIBTimeForDirectus().split('T')[0]}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-muted-foreground mb-2">
                  {t('interactions.interaction_detail_modal.interaction_type', 'Interaction Type')} *
                </label>
                <select
                  value={rescheduleType}
                  onChange={(e) => setRescheduleType(e.target.value as 'WhatsApp' | 'Phone Call' | 'Visit/Meeting')}
                  className="w-full px-4 py-3 border border-border rounded-lg focus:ring-2 focus:ring-ring focus:border-ring bg-background text-foreground"
                >
                  <option value="WhatsApp">{t('interactions.interaction_detail_modal.whatsapp', 'WhatsApp')}</option>
                  <option value="Phone Call">{t('interactions.interaction_detail_modal.phone_call', 'Phone Call')}</option>
                  <option value="Visit/Meeting">{t('interactions.interaction_detail_modal.visit_meeting', 'Visit/Meeting')}</option>
                </select>
              </div>
              <div className="flex space-x-3">
                <button
                  onClick={() => setShowRescheduleDialog(false)}
                  className="flex-1 px-4 py-2 border border-border text-muted-foreground rounded-lg font-medium hover:bg-muted/50 transition-colors"
                >
                  {t('interactions.interaction_detail_modal.cancel')}
                </button>
                <button
                  onClick={handleReschedule}
                  disabled={!rescheduleDate || !rescheduleType}
                  className="flex-1 px-4 py-2 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('interactions.interaction_detail_modal.update_date')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Phone Number Selection Modal */}
      {showPhoneModal && (
        <div className="fixed inset-0 bg-black bg-black/50 flex items-center justify-center z-60 p-4">
          <div className="bg-card rounded-xl p-6 w-full max-w-md max-h-[80vh] overflow-y-auto border border-border">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">
                Contact {interaction.customer_name}
              </h3>
              <button
                onClick={() => {
                  setShowPhoneModal(false);
                  setSelectedAction(null);
                }}
                className="p-1 hover:bg-muted/50 rounded"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-muted-foreground">Customer ID: {interaction.No_pelanggan}</p>
            </div>

            {phoneLoading ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                <span className="ml-2 text-muted-foreground">Loading phone numbers...</span>
              </div>
            ) : phoneError ? (
              <div className="text-center py-8">
                <p className="text-red-500 mb-4">{phoneError}</p>
                <button
                  onClick={fetchPhoneNumbers}
                  className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90"
                >
                  Try Again
                </button>
              </div>
            ) : phoneNumbers.length === 0 ? (
              <div className="text-center py-8">
                <Phone className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No phone numbers found for {interaction.customer_name}</p>
                <p className="text-sm text-muted-foreground mt-2">
                  Check customer data in Phone_Customer or Customer_tambak collections
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                <h4 className="font-medium text-muted-foreground mb-3">
                  Choose how to contact this number:
                </h4>
                {phoneNumbers.map((phoneNumber, index) => (
                  <div key={index} className="border border-border rounded-lg p-3 bg-muted/50">
                    <div className="flex items-center space-x-3 mb-3">
                      {getPhoneTypeIcon(phoneNumber.type)}
                      <div className="flex-1">
                        <p className="font-medium">{formatPhoneNumber(phoneNumber.number)}</p>
                        <p className="text-sm text-muted-foreground capitalize">
                          {phoneNumber.source.replace('_', ' ')}
                        </p>
                      </div>
                    </div>

                    <div className="flex space-x-2">
                      <button
                        onClick={() => handlePhoneNumberSelect(phoneNumber, 'call')}
                        className="flex-1 px-3 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center space-x-2"
                      >
                        <Phone className="w-4 h-4" />
                        <span>{t('all_customers.call')}</span>
                      </button>

                      {phoneNumber.type === 'whatsapp' ? (
                        <button
                          onClick={() => handlePhoneNumberSelect(phoneNumber, 'whatsapp')}
                          className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>{t('all_customers.whatsapp')}</span>
                        </button>
                      ) : (
                        <button
                          onClick={() => handlePhoneNumberSelect(phoneNumber, 'whatsapp')}
                          className="flex-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors flex items-center justify-center space-x-2"
                        >
                          <MessageCircle className="w-4 h-4" />
                          <span>{t('all_customers.whatsapp')}</span>
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-border">
              <button
                onClick={() => {
                  setShowPhoneModal(false);
                  setSelectedAction(null);
                }}
                className="w-full py-2 px-4 bg-muted text-muted-foreground rounded-lg hover:bg-muted/80 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Customer Details Modal for Visit action */}
      {selectedCustomer && (
        <CustomerDetailsModal
          customer={selectedCustomer}
          isOpen={showCustomerDetails}
          onClose={() => {
            setShowCustomerDetails(false);
            if (!showCustomerSelection) {
              setSelectedCustomer(null);
            }
          }}
          onCall={handleCustomerDetailsCall}
          onWhatsApp={handleCustomerDetailsWhatsApp}
          onStartVisit={handleStartVisit}
        />
      )}

      {/* Customer Selection Modal for Visit action */}
      {selectedCustomer && (
        <CustomerSelectionModal
          customer={selectedCustomer}
          isOpen={showCustomerSelection}
          onClose={() => {
            setShowCustomerSelection(false);
            setSelectedCustomer(null);
            setCustomerAction(null);
          }}
          actionType={customerAction}
          onAction={handleCustomerActionComplete}
        />
      )}

      {/* Notes Modal for keterangan workflow */}
      <NotesModal
        isOpen={showNotesModal}
        onClose={handleNotesClose}
        onSave={handleNotesSave}
        onActionSelect={handleActionSelect}
        title={t('interactions.instant_visit_modal.add_notes_contact')}
        placeholder={t('interactions.instant_visit_modal.add_notes_placeholder')}
        initialValue={interaction?.keterangan || ''}
        actionType={pendingAction?.type}
        customerName={interaction?.customer_name}
        phoneNumber={pendingAction?.phoneNumber?.number}
        showActionSelection={false}
        enableFileUpload={true}
        enableProgressSelection={true}
        initialProgress={interaction?.Progress || null}
        customerPhoneNumbers={phoneNumbers.map(p => p.number)}
        onDirectContact={handleDirectContact}
      />

      {/* Append Details Modal - For completed interactions within 7-day window or overdue interactions */}
      <AppendDetailsModal
        isOpen={showAppendModal}
        onClose={() => setShowAppendModal(false)}
        onSave={handleAppendDetails}
        customerName={interaction?.customer_name || ''}
        completedDate={interaction?.completed_date}
        createdDate={interactionStatus.status === 'overdue' ? interaction?.date_created : undefined}
        currentDetails={interaction?.keterangan}
      />

      {/* Notes Detail Modal - For viewing full notes */}
      <NotesDetailModal
        isOpen={showNotesDetailModal}
        onClose={() => setShowNotesDetailModal(false)}
        notes={interaction?.keterangan || ''}
        title={`${interaction?.customer_name}'s Notes`}
      />

    </div>
  );
};